import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Phone, ShoppingCart } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV, COMPANY } from "@/data/site";
import { useCart } from "@/context/CartContext";

export default function PublicHeader() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const cart = useCart();

  return (
    <header className="sticky top-0 z-50 bg-ei-white/90 backdrop-blur-md border-b border-ei-mist">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4 min-h-[80px]">
          <Link to="/" aria-label="EI Battery home" className="flex-shrink-0 py-3" data-testid="header-logo-link">
            <Logo width={260} />
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            <button
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              className="font-display text-sm font-bold text-ei-navy hover:text-ei-golddark px-4 py-3 rounded-sm border border-ei-mist hover:border-ei-golddark transition-colors min-h-[44px] inline-flex items-center gap-2"
              data-testid="desktop-menu-toggle"
            >
              {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />} Menu
            </button>
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/cart"
              className="relative font-display text-sm font-semibold text-ei-navy hover:text-ei-golddark px-2 py-3 min-h-[44px] inline-flex items-center gap-2"
              data-testid="header-cart-link"
            >
              <ShoppingCart className="w-5 h-5" />
              {cart?.count > 0 && (
                <span className="absolute -top-0.5 -right-1 min-w-[18px] h-[18px] rounded-full bg-ei-golddark text-[11px] font-bold text-white inline-flex items-center justify-center px-1 tnum" data-testid="header-cart-count">
                  {cart.count}
                </span>
              )}
            </Link>
            <Link
              to="/request-a-quote"
              className="font-display text-sm font-bold bg-ei-navy text-ei-white px-5 py-3 rounded-sm hover:bg-ei-ink transition-colors min-h-[44px] inline-flex items-center"
              data-testid="header-quote-cta"
            >
              Request a quote
            </Link>
            <Link
              to="/account/login"
              className="font-display text-sm font-semibold text-ei-navy hover:text-ei-golddark px-3 py-3 min-h-[44px] inline-flex items-center border-l border-ei-mist"
              data-testid="header-trade-login"
            >
              Login
            </Link>
          </div>

          <button
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 text-ei-navy"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-ei-mist bg-ei-white shadow-lg lg:border-t-0 lg:bg-transparent lg:shadow-none lg:absolute lg:inset-x-0" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-col max-h-[70vh] overflow-y-auto overscroll-contain lg:max-h-[60vh] lg:w-80 lg:mx-0 lg:ml-[19.5rem] lg:my-3 lg:px-4 lg:border lg:border-ei-mist lg:rounded-md lg:bg-ei-white lg:shadow-xl" data-testid="nav-dropdown-scroll">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `font-display text-base font-semibold py-3 min-h-[44px] flex items-center ${
                    isActive ? "text-ei-golddark" : "text-ei-navy"
                  }`
                }
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.label}
              </NavLink>
            ))}
            <button
              onClick={() => { setOpen(false); navigate("/request-a-quote"); }}
              className="mt-3 font-display font-bold bg-ei-navy text-ei-white px-5 py-3 rounded-sm text-center lg:hidden"
              data-testid="mobile-quote-cta"
            >
              Request a quote
            </button>
            <Link to="/cart" onClick={() => setOpen(false)} className="mt-2 font-display font-semibold text-ei-navy py-3 inline-flex items-center gap-2 lg:hidden" data-testid="mobile-cart-link">
              <ShoppingCart className="w-4 h-4" /> Cart{cart?.count > 0 ? ` (${cart.count})` : ""}
            </Link>
            <Link to="/account/login" onClick={() => setOpen(false)} className="mt-2 font-display font-semibold text-ei-navy py-3 lg:hidden" data-testid="mobile-trade-login">
              Login
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
